import { UNIFIED_ACCOUNT_ID, STANDARD_UNIFIED_ROLES, getStandardRoleDisplayName, type StandardUnifiedRole } from '@/constants/mailbox';
import { inferUnifiedRole } from '@/lib/folderRoles';
import type { Account, AccountStatus, Folder } from '@/types/email';

export function normalizeText(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

function titleCase(value: string): string {
  return value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'folder';
}

export function buildUnifiedAccount(rawAccounts: Account[]): Account | null {
  if (!rawAccounts.length) {
    return null;
  }

  let status: AccountStatus = 'idle';
  if (rawAccounts.some((account) => account.status === 'error')) {
    status = 'error';
  } else if (rawAccounts.some((account) => account.status === 'syncing')) {
    status = 'syncing';
  }

  const unreadCount = rawAccounts.reduce((total, account) => total + Math.max(0, account.unreadCount ?? 0), 0);
  const latestSyncedAt = rawAccounts
    .map((account) => (account.lastSyncedAt ? new Date(account.lastSyncedAt).getTime() : null))
    .filter((value): value is number => value !== null)
    .reduce((max, value) => (value > max ? value : max), 0);

  return {
    id: UNIFIED_ACCOUNT_ID,
    emailAddress: 'All Accounts',
    displayName: 'Unified Inbox',
    provider: null,
    status,
    lastSyncedAt: latestSyncedAt ? new Date(latestSyncedAt).toISOString() : null,
    errorMessage: null,
    unreadCount,
    color: null,
  };
}

export function mergeWithUnifiedAccounts(rawAccounts: Account[]): Account[] {
  if (rawAccounts.some((account) => account.id === UNIFIED_ACCOUNT_ID)) {
    return rawAccounts;
  }
  const unified = buildUnifiedAccount(rawAccounts);
  return unified ? [unified, ...rawAccounts] : rawAccounts;
}

export function mapAccountFolders(accountId: string, folders: Folder[]): Folder[] {
  return folders.map((folder) => ({
    ...folder,
    sourceFolders: [{ accountId, folderId: folder.id }],
  }));
}

type SourceFolderRef = {
  accountId: string;
  folderId: string;
};

type AggregateSourceMeta = {
  ref: SourceFolderRef;
  unread: number;
  total: number;
  priority: number;
  hidden: boolean;
};

type AggregateInfo = {
  role?: StandardUnifiedRole;
  name: string;
  icon?: string;
  unread: number;
  total: number;
  sources: SourceFolderRef[];
  sourceMetaByAccount?: Map<string, AggregateSourceMeta>;
  hiddenSources?: number;
  totalSources?: number;
};

const getStandardFolderPriority = (folder: Folder, role: StandardUnifiedRole): number => {
  let priority = 0;

  if (folder.unifiedRole === role) {
    priority += 200;
  }

  const normalizedRole = normalizeText(folder.role || null);
  if (normalizedRole === role) {
    priority += 100;
  }

  if (folder.systemFolder) {
    priority += 50;
  }

  const normalizedName = normalizeText(folder.name);
  if (normalizedName === role) {
    priority += 10;
  }

  const normalizedPath = normalizeText(folder.path);
  if (normalizedPath === role) {
    priority += 8;
  } else if (normalizedPath.includes(role)) {
    priority += 2;
  }

  const depth = (folder.path || '')
    .split(/[\\/]/)
    .filter(Boolean).length;
  priority += Math.max(0, 5 - Math.min(depth, 5));

  return priority;
};

export function buildUnifiedFolders(
  foldersByAccount: Record<string, Folder[]>,
  accounts: Account[],
): Folder[] {
  const standardAggregates = new Map<StandardUnifiedRole, AggregateInfo>();
  const customAggregates = new Map<string, AggregateInfo>();

  for (const role of STANDARD_UNIFIED_ROLES) {
    standardAggregates.set(role, {
      role,
      name: getStandardRoleDisplayName(role),
      unread: 0,
      total: 0,
      sources: [],
      sourceMetaByAccount: new Map(),
    });
  }

  for (const account of accounts) {
    const accountFolders = foldersByAccount[account.id] || [];
    for (const folder of accountFolders) {
      const normalizedName = normalizeText(folder.name);
      const normalizedPath = normalizeText(folder.path);
      const role = inferUnifiedRole(folder);
      const sourceRef: SourceFolderRef = { accountId: account.id, folderId: folder.id };

      const shouldTreatAsStandard =
        Boolean(role)
        && (
          folder.unifiedRole === role
          || normalizedName === role
          || normalizedPath === role
        );

      if (role && shouldTreatAsStandard) {
        const aggregate = standardAggregates.get(role);
        if (aggregate && aggregate.sourceMetaByAccount) {
          const unreadValue = folder.unreadCount ?? folder.count ?? 0;
          const totalValue = folder.totalCount ?? 0;
          const priority = getStandardFolderPriority(folder, role);
          const existing = aggregate.sourceMetaByAccount.get(account.id);
          const candidate: AggregateSourceMeta = {
            ref: sourceRef,
            unread: unreadValue,
            total: totalValue,
            priority,
            hidden: Boolean(folder.hidden),
          };

          const shouldReplace =
            !existing
            || priority > existing.priority
            || (priority === existing.priority && unreadValue > existing.unread)
            || (priority === existing.priority && unreadValue === existing.unread && totalValue > existing.total);

          if (shouldReplace) {
            if (existing) {
              aggregate.unread -= existing.unread;
              aggregate.total -= existing.total;
            }
            aggregate.unread += unreadValue;
            aggregate.total += totalValue;
            aggregate.sourceMetaByAccount.set(account.id, candidate);
            const entries = Array.from(aggregate.sourceMetaByAccount.values());
            aggregate.sources = entries.map((entry) => entry.ref);
            aggregate.totalSources = entries.length;
            aggregate.hiddenSources = entries.reduce((count, entry) => (entry.hidden ? count + 1 : count), 0);
            if (folder.icon) {
              aggregate.icon = folder.icon;
            }
          } else if (!aggregate.icon && folder.icon) {
            aggregate.icon = folder.icon;
          }
        }
        continue;
      }

      const customKey = `custom:${normalizedName || folder.path || folder.id}`;
      if (!customAggregates.has(customKey)) {
        const fallbackName =
          folder.name?.trim()
          || (normalizedName ? titleCase(normalizedName) : null)
          || folder.path
          || 'Custom Folder';
        customAggregates.set(customKey, {
          name: fallbackName,
          icon: folder.icon,
          unread: 0,
          total: 0,
          sources: [],
          hiddenSources: 0,
          totalSources: 0,
        });
      }

      const aggregate = customAggregates.get(customKey)!;
      aggregate.sources.push(sourceRef);
      aggregate.total += folder.totalCount ?? 0;
      aggregate.unread += folder.unreadCount ?? folder.count ?? 0;
      aggregate.totalSources = (aggregate.totalSources ?? 0) + 1;
      if (folder.hidden) {
        aggregate.hiddenSources = (aggregate.hiddenSources ?? 0) + 1;
      }
      if (!aggregate.icon && folder.icon) {
        aggregate.icon = folder.icon;
      }
      if (!aggregate.role) {
        const inferredRole = inferUnifiedRole(folder);
        if (inferredRole) {
          aggregate.role = inferredRole;
        }
      }
    }
  }

  const unifiedFolders: Folder[] = [];
  const customSourcesByAccount = new Map<string, SourceFolderRef[]>();

  for (const role of STANDARD_UNIFIED_ROLES) {
    const aggregate = standardAggregates.get(role)!;
    if (!aggregate.sources.length) {
      continue;
    }
    unifiedFolders.push({
      id: `${UNIFIED_ACCOUNT_ID}:${role}`,
      accountId: UNIFIED_ACCOUNT_ID,
      name: aggregate.name,
      path: role.toUpperCase(),
      role,
      unifiedRole: role,
      icon: aggregate.icon || 'inbox',
      systemFolder: true,
      count: aggregate.unread,
      unreadCount: aggregate.unread,
      hidden: false,
      sourceFolders: aggregate.sources,
    });
    aggregate.sources.forEach((source) => {
      customSourcesByAccount.set(source.accountId, [
        ...(customSourcesByAccount.get(source.accountId) ?? []),
        source,
      ]);
    });
  }

  for (const [key, aggregate] of customAggregates.entries()) {
    const slug = slugify(key.replace(/^custom:/, ''));
    const uniqueAccounts = new Set(aggregate.sources.map((source) => source.accountId));
    let displayName = aggregate.name || 'All Folders';
    if (uniqueAccounts.size <= 1) {
      const source = aggregate.sources[0];
      const folder = (foldersByAccount[source.accountId] || []).find((candidate) => candidate.id === source.folderId);
      if (folder) {
        displayName = folder.name;
      }
    }
    const totalSources = aggregate.totalSources ?? aggregate.sources.length;
    const hiddenSources = aggregate.hiddenSources ?? 0;
    const aggregateHidden = totalSources > 0 && hiddenSources === totalSources;
    unifiedFolders.push({
      id: `${UNIFIED_ACCOUNT_ID}:custom:${slug}`,
      accountId: UNIFIED_ACCOUNT_ID,
      name: displayName,
      path: `CUSTOM:${slug.toUpperCase()}`,
      role: undefined,
      unifiedRole: aggregate.role ?? null,
      icon: aggregate.icon || 'inbox',
      systemFolder: false,
      count: aggregate.unread,
      unreadCount: aggregate.unread,
      hidden: aggregateHidden,
      sourceFolders: aggregate.sources,
    });
    aggregate.sources.forEach((source) => {
      customSourcesByAccount.set(source.accountId, [
        ...(customSourcesByAccount.get(source.accountId) ?? []),
        source,
      ]);
    });
  }

  for (const account of accounts) {
    const accountFolders = foldersByAccount[account.id] || [];
    const accountedSources = new Set(
      (customSourcesByAccount.get(account.id) ?? []).map((source) => `${source.accountId}:${source.folderId}`)
    );

    for (const folder of accountFolders) {
      const aggregateKey = `${account.id}:${folder.id}`;
      const isInStandard = STANDARD_UNIFIED_ROLES.some((role) =>
        standardAggregates.get(role)?.sources.some((source) => `${source.accountId}:${source.folderId}` === aggregateKey)
      );
      const isInCustom = accountedSources.has(aggregateKey);

      if (isInStandard || isInCustom) {
        continue;
      }

      unifiedFolders.push({
        ...folder,
        name: folder.name,
        id: `${account.id}:${folder.id}`,
        accountId: UNIFIED_ACCOUNT_ID,
        systemFolder: Boolean(folder.systemFolder && inferUnifiedRole(folder)),
        sourceFolders: [{ accountId: account.id, folderId: folder.id }],
      });
    }
  }

  return unifiedFolders;
}
